import { useState, useEffect } from 'react';
import { api, endpoints } from '../config/api';
import { useAuth } from '../context/AuthContext';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';

const DAYS_ES = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];

function getFechaTexto(fechaInicio, day) {
  if (!fechaInicio) return null;
  const fechaStr = String(fechaInicio).slice(0, 10);
  if (!fechaStr || fechaStr.length < 10) return null;
  const d = new Date(fechaStr + 'T12:00:00');
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + (Number(day) || 1) - 1);
  return `${DAYS_ES[d.getDay()]} ${d.getDate()}`;
}

function AgendasPage() {
  const { token } = useAuth();
  const [data, setData] = useState([]);
  const [events, setEvents] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ eventId: '', time: '', title: '', description: '', speakerId: '', day: 1, fechaTexto: '' });

  const load = async () => {
    try {
      const [a, e] = await Promise.all([api.get(endpoints.agendas(), token), api.get(endpoints.events(), token)]);
      setData(a);
      setEvents(e);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const fetchSpeakers = async () => {
      if (!form.eventId) {
        setSpeakers([]);
        return;
      }
      try {
        const list = await api.get(endpoints.speakersByEvent(form.eventId), token);
        setSpeakers(Array.isArray(list) ? list : []);
      } catch {
        setSpeakers([]);
      }
    };
    fetchSpeakers();
  }, [form.eventId, token]);

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      eventId: form.eventId,
      time: form.time,
      title: form.title,
      description: form.description || null,
      speakerId: form.speakerId || null,
      day: form.day ?? 1,
      fechaTexto: form.fechaTexto || null,
    };
    try {
      if (editing) await api.put(endpoints.agenda(editing.id), payload, token);
      else await api.post(endpoints.agendas(), payload, token);
      setModalOpen(false);
      setEditing(null);
      setForm({ eventId: '', time: '', title: '', description: '', speakerId: '', day: 1, fechaTexto: '' });
      load();
    } catch (err) {
      alert(err?.message || 'Error');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm('¿Eliminar?')) return;
    try {
      await api.delete(endpoints.agenda(row.id), token);
      load();
    } catch (err) {
      alert(err?.message || 'Error');
    }
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'fechaTexto', label: 'Fecha' },
    { key: 'time', label: 'Hora' },
    { key: 'title', label: 'Título' },
    { key: 'speakerId', label: 'Speaker', render: (v) => v || '—' },
    { key: 'eventId', label: 'Evento' },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Agenda</h1>
        <button onClick={() => { setEditing(null); setForm({ eventId: events[0]?.id || '', time: '', title: '', description: '', speakerId: '', day: 1, fechaTexto: '' }); setModalOpen(true); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Nuevo</button>
      </div>
      {loading ? <p>Cargando...</p> : <DataTable columns={columns} data={data} onEdit={(r) => { setEditing(r); setForm(r); setModalOpen(true); }} onDelete={handleDelete} />}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar' : 'Nueva agenda'}>
        <form onSubmit={handleSave} className="space-y-4">
          <div><label className="block text-sm font-medium mb-1">Evento</label><select value={form.eventId} onChange={(e) => setForm((f) => ({ ...f, eventId: e.target.value, speakerId: '' }))} className="w-full px-3 py-2 border rounded" required>{events.map((ev) => <option key={ev.id} value={ev.id}>{ev.nombre}</option>)}</select></div>
          <div><label className="block text-sm font-medium mb-1">Día</label><select value={form.day ?? 1} onChange={(e) => setForm((f) => ({ ...f, day: Number(e.target.value) }))} className="w-full px-3 py-2 border rounded">{[1, 2, 3, 4, 5, 6, 7].map((d) => <option key={d} value={d}>Día {d}</option>)}</select></div>
          <div>
            <label className="block text-sm font-medium mb-1">Fecha</label>
            <input type="text" value={form.fechaTexto || ''} onChange={(e) => setForm((f) => ({ ...f, fechaTexto: e.target.value }))} placeholder="ej. SÁBADO 14, VIERNES 13" className="w-full px-3 py-2 border rounded" />
            {(() => {
              const ev = events.find((e) => String(e.id) === String(form.eventId));
              const suggested = ev?.fechaInicio ? getFechaTexto(String(ev.fechaInicio), form.day ?? 1) : null;
              return suggested ? <p className="text-xs text-gray-500 mt-1">Sugerido según evento: {suggested}</p> : null;
            })()}
          </div>
          <div><label className="block text-sm font-medium mb-1">Speaker</label><select value={form.speakerId || ''} onChange={(e) => setForm((f) => ({ ...f, speakerId: e.target.value || null }))} className="w-full px-3 py-2 border rounded"><option value="">— Sin asignar —</option>{speakers.map((s) => <option key={s.id} value={s.id}>{s.nombre || s.name}</option>)}</select></div>
          <div><label className="block text-sm font-medium mb-1">Hora</label><input value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} placeholder="09:00" className="w-full px-3 py-2 border rounded" required /></div>
          <div><label className="block text-sm font-medium mb-1">Título</label><input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="w-full px-3 py-2 border rounded" required /></div>
          <div><label className="block text-sm font-medium mb-1">Descripción</label><textarea value={form.description || ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 border rounded" rows={3} /></div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border rounded">Cancelar</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Guardar</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default AgendasPage;
